import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, Chip, FormControlLabel, LinearProgress, MenuItem, Slider,
    Switch, TextField, Tooltip, Typography
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { toast } from "react-toastify";
import moment from "moment";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
import { vlearnInputSx, vlearnSurface, vlearnType } from "./vlearnUi";
import { shrinkImageFile, formatBytes } from "../../app/utils/imageResize";
import { saveVLearnCourse, setSaveState, uploadVLearnCover } from "../../containers/vlearn/vlearnSlice";

/**
 * V-Learning — the "Details" tab: everything about a course that is not its content.
 *
 * ── Save-as-you-go ───────────────────────────────────────────────────────────────────
 *
 * There is no Save button, for the reason V-Resume's editor has none: a course description
 * is written over several sittings, and an unsaved-changes state that a closed tab can
 * destroy is the wrong model for that. Every edit updates local state immediately — so the
 * field stays responsive instead of waiting on a round trip — and queues a debounced write.
 *
 * Pending edits accumulate in a ref rather than in state because the timer callback needs
 * the value as it is when the timer FIRES; a state variable captured in the closure would be
 * the value from when the timer was set, which for a field being typed into is always stale.
 *
 * `saveCourse` writes only the keys present in the body, so sending one field never blanks
 * the others — which is what makes a per-field debounce safe.
 */

/**
 * Long enough that a burst of typing is one request, short enough that "saved" appears while
 * the author is still looking at the field. The same 700ms V-Resume settled on.
 */
const AUTOSAVE_DEBOUNCE_MS = 700

const LOCALES = [
    { value: 'fr', key: 'VLEARN_LOCALE_FR' },
    { value: 'en', key: 'VLEARN_LOCALE_EN' }
]

const LEVELS = [
    { value: 'beginner', key: 'VLEARN_LEVEL_BEGINNER' },
    { value: 'intermediate', key: 'VLEARN_LEVEL_INTERMEDIATE' },
    { value: 'advanced', key: 'VLEARN_LEVEL_ADVANCED' }
]

/** What the backend's cover uploader accepts — jpeg, png and webp, and nothing else. */
const COVER_ACCEPT = 'image/jpeg,image/png,image/webp'

/** See the identical note in CourseEditor: two thunk families, two error shapes. */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

/*
 * Availability is a DATE, not a datetime.
 *
 * These were `datetime-local` inputs, which asked the author for a time of day that was then
 * thrown away: `saveCourse` formats both through `getDbFormattedDate`, which yields
 * `YYYY-MM-DD`. So "closes at 18:00 on the 1st" silently became "closes at 00:00 on the 1st"
 * — the control promised a precision the storage never had.
 *
 * Asking for the date alone is the honest version, and it is what a course schedule needs.
 * `available_to` is INCLUSIVE — the whole of that day — enforced server-side in
 * `VLearnBaseController.isAvailable`.
 */
const toDateInput = (value) => {
    if (!value) return ''
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

/** The course row reduced to the fields this form owns. */
const fromCourse = (course) => ({
    title: course.title || '',
    subtitle: course.subtitle || '',
    description: course.description || '',
    objectives: course.objectives || '',
    id_category: course.id_category || '',
    locale: course.locale || 'fr',
    level: course.level || 'beginner',
    is_mandatory: !!Number(course.is_mandatory),
    sequential: !!Number(course.sequential),
    certificate_enabled: !!Number(course.certificate_enabled),
    estimated_minutes: course.estimated_minutes || '',
    passing_percent: Number(course.passing_percent) || 100,
    available_from: toDateInput(course.available_from),
    available_to: toDateInput(course.available_to),
    due_date: toDateInput(course.due_date)
})

export default function CourseDetailsForm({ course, onSaved }) {

    const dispatch = useDispatch()

    const categories = useSelector((state) => state.vlearn.categories)

    const uploadProgress = useSelector((state) => state.vlearn.uploadProgress)
    const lang = useSelector((state) => state.settings.lang)

    const idCourse = course.id
    const [form, setForm] = useState(() => fromCourse(course))

    // The course's category when it is no longer among the options offered — see the note at
    // the Select. Compared loosely because ids arrive as strings from the driver and as
    // numbers from a fresh pick.
    const retiredCategory = useMemo(() => {
        const current = form.id_category
        if (current === '' || current === null || current === undefined) return null
        const known = (categories || []).some(c => String(c.id) === String(current))
        return known ? null : current
    }, [categories, form.id_category])

    const [uploading, setUploading] = useState(false)
    const fileInput = useRef(null)

    const timer = useRef(null)
    const pending = useRef({})
    /**
     * The payload the debounce is about to send, kept on a ref for the reason given in the
     * file comment. `idCourse` rides on a ref too so the unmount flush below does not need
     * the course in its dependency array — re-registering that cleanup on every keystroke
     * would run it on every keystroke.
     */
    const courseRef = useRef(idCourse)
    courseRef.current = idCourse
    /**
     * The form as it stands right now.
     *
     * `flush` needs to compare `available_from` against `available_to` at the moment it
     * fires, but it must NOT change identity when either of them changes: the unmount flush
     * below is registered against it, and a `flush` that is a new function on every keystroke
     * would make that cleanup run — and therefore save — on every keystroke.
     */
    const formRef = useRef(form)
    formRef.current = form

    // Switching to another course replaces the form wholesale. Keyed on the id alone: the
    // course object is a new reference after every autosave, and depending on it would
    // overwrite whatever the author has typed since.
    useEffect(() => { setForm(fromCourse(course)) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [idCourse])

    const flush = useCallback(() => {
        const payload = pending.current
        if (!Object.keys(payload).length) return

        // A write the server is certain to refuse is not sent, and it is not thrown away
        // either — it stays queued so the next edit carries it along once the problem is
        // fixed. The two conditions are the ones `saveCourse` checks, an empty title and an
        // inverted availability window, and both are already reported next to the field they
        // belong to; a round trip would add nothing but a duplicate message.
        const blankTitle = typeof payload.title !== 'undefined'
            && !String(payload.title).trim()
        const from = typeof payload.available_from !== 'undefined'
            ? payload.available_from : formRef.current.available_from
        const to = typeof payload.available_to !== 'undefined'
            ? payload.available_to : formRef.current.available_to
        const invertedRange = !!(from && to && new Date(from) > new Date(to))
        if (blankTitle || invertedRange) return

        pending.current = {}
        dispatch(setSaveState('saving'))
        dispatch(saveVLearnCourse({ id_course: courseRef.current, ...payload }))
            .then((response) => {
                const data = response.payload && response.payload.responseData
                if (data && data.status === 1) {
                    dispatch(setSaveState('saved'))
                    if (onSaved) onSaved()
                } else {
                    dispatch(setSaveState('error'))
                    const message = serverMessage(response.payload)
                    if (message) toast.error(message)
                }
            })
    }, [dispatch, onSaved])

    /**
     * Fires the pending write instead of discarding it.
     *
     * Leaving the tab inside the debounce window is the single most likely moment for an
     * edit to be lost, because switching tab is what an author does the instant they finish
     * typing. `clearTimeout` alone would drop exactly that edit; flushing sends it. The
     * dispatch survives this component — the store and the thunk do not care that the form
     * has gone.
     */
    useEffect(() => {
        const timerRef = timer
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            flush()
        }
    }, [flush])

    /** Applies a change locally and queues the debounced write. */
    const edit = useCallback((changes) => {
        setForm(prev => ({ ...prev, ...changes }))
        pending.current = { ...pending.current, ...changes }
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS)
    }, [flush])

    /**
     * The client's copy of two checks the server also makes.
     *
     * This duplication is deliberate and it is not a guarantee — the server's is. `saveCourse`
     * refuses an empty title and an `available_from` later than `available_to`, and it is
     * right to: a request can arrive from anywhere. What it cannot do is put the message next
     * to the field. That is the client's job, and doing it here also spares a round trip
     * whose only possible outcome is a 422 the author already knows about.
     */
    const titleError = !String(form.title || '').trim()
    const rangeError = useMemo(() => {
        if (!form.available_from || !form.available_to) return false
        return new Date(form.available_from) > new Date(form.available_to)
    }, [form.available_from, form.available_to])

    const onPickCover = async (event) => {
        const file = event.target.files && event.target.files[0]
        // Reset immediately so choosing the same file twice still fires a change event.
        event.target.value = ''
        if (!file) return
        setUploading(true)

        /*
         * Shrink before uploading, rather than letting the server refuse a large image.
         *
         * A cover is rendered as a ~270px catalogue tile and a course-page banner, so nothing
         * above 1600px on the long edge is ever seen — while a phone photo is routinely 4000px
         * and 8 MB, which the endpoint rejected outright ("Image is too large"). Telling an
         * author to go and resize their own picture is work the browser can just do.
         *
         * It happens here and not on the server because there is no image library in this
         * project on either side, deliberately; `<canvas>` is native and costs no dependency.
         * The server keeps every check it had — magic bytes, byte cap, MAX_IMAGE_SIDE, EXIF
         * strip — because a client-side resize is a convenience, never a control.
         */
        const upload = await shrinkImageFile(file)
        const shrunk = upload !== file

        const response = await dispatch(uploadVLearnCover({ file: upload, idCourse }))
        setUploading(false)
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            // Say so when it happened: an author who picked an 8 MB file and got a success
            // toast should know the stored image is not the one they chose.
            toast.success(shrunk
                ? Localize("VLEARN_COVER_UPLOADED_RESIZED", lang)
                    .replace('%from%', formatBytes(file.size))
                    .replace('%to%', formatBytes(upload.size))
                : Localize("VLEARN_COVER_UPLOADED", lang))
            // The cover URL is cache-busted with the course's `updated_at`, so the new image
            // only appears once the course row has been read back.
            if (onSaved) onSaved()
        } else {
            toast.error(serverMessage(response.payload) || Localize("VLEARN_UPLOAD_FAILED", lang))
        }
    }

    const coverUrl = course.cover_filename
        ? NetworkManager.getInstance().getVLearnCoverUrl(idCourse, course.updated_at)
        : null

    // 16px below `sm`: iOS Safari zooms the whole page when a focused input's
    // computed font-size is under 16px, and it does not zoom back out.
    const inputSx = vlearnInputSx
    const labelProps = { sx: { fontSize: '0.86rem' } }

    /** Two columns from `md` up, one below. No separate mobile component. */
    const grid = {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: '16px'
    }

    const groupTitle = (key, hintKey) => (
        <Box sx={{ mb: 1.5 }}>
            <Typography component="h2" sx={vlearnType.sectionTitle}><Langs str={key} /></Typography>
            {hintKey ? (
                <Typography sx={{ ...vlearnType.body, mt: 0.25 }}><Langs str={hintKey} /></Typography>
            ) : null}
        </Box>
    )

    /**
     * `helpKeyOff` makes the description follow the SWITCH, which for `sequential` it has to.
     *
     * A single static help text has to describe both states at once, and the sequential one
     * opened by asserting the locked behaviour — "a lesson stays locked until the mandatory
     * lessons before it are finished" — which reads as a statement of current fact even with
     * the switch off. An author reasonably reported that as a bug: the toggle said no, the
     * text said the course is taken in order.
     *
     * So a switch with two genuinely different consequences gets two descriptions, and the
     * one on screen is the one that is true right now.
     */
    const switchRow = (field, labelKey, helpKey, helpKeyOff) => (
        <Box>
            <FormControlLabel
                control={
                    <Switch size="small" checked={!!form[field]}
                        onChange={(event) => edit({ [field]: event.target.checked })} />
                }
                label={<Typography sx={{ fontSize: '0.86rem' }}><Langs str={labelKey} /></Typography>}
            />
            <Typography sx={{ ...vlearnType.meta, ml: 5.5, mt: -0.5 }}>
                <Langs str={(helpKeyOff && !form[field]) ? helpKeyOff : helpKey} />
            </Typography>
        </Box>
    )

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* ── Identity ───────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                {groupTitle('VLEARN_GROUP_IDENTITY', 'VLEARN_GROUP_IDENTITY_HELP')}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TextField
                        size="small" fullWidth required
                        label={<Langs str="VLEARN_FIELD_TITLE" />}
                        value={form.title}
                        // 190 is the column's width, and the server clips silently at it —
                        // letting the author type a 300-character title and quietly storing
                        // 190 of it is worse than stopping the keystroke.
                        inputProps={{ maxLength: 190 }}
                        onChange={(event) => edit({ title: event.target.value })}
                        error={titleError}
                        helperText={titleError
                            ? <Langs str="VLEARN_FIELD_TITLE_REQUIRED" />
                            : <Langs str="VLEARN_FIELD_TITLE_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                    <TextField
                        size="small" fullWidth
                        label={<Langs str="VLEARN_FIELD_SUBTITLE" />}
                        value={form.subtitle}
                        inputProps={{ maxLength: 255 }}
                        onChange={(event) => edit({ subtitle: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_SUBTITLE_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                    <TextField
                        size="small" fullWidth multiline minRows={4}
                        label={<Langs str="VLEARN_FIELD_DESCRIPTION" />}
                        value={form.description}
                        onChange={(event) => edit({ description: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_DESCRIPTION_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                    <TextField
                        size="small" fullWidth multiline minRows={3}
                        label={<Langs str="VLEARN_FIELD_OBJECTIVES" />}
                        value={form.objectives}
                        onChange={(event) => edit({ objectives: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_OBJECTIVES_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                </Box>

                <Box sx={{ ...grid, mt: 2 }}>
                    <TextField
                        select size="small" fullWidth
                        label={<Langs str="VLEARN_FIELD_CATEGORY" />}
                        value={form.id_category}
                        onChange={(event) => edit({ id_category: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_CATEGORY_HELP" />}
                        sx={inputSx}
                        /* `shrink` is forced because "no category" is a legitimate value of ''.
                           Left to itself the label drops back down over an empty field, so the
                           control reads as broken rather than as deliberately empty. */
                        InputLabelProps={{ ...labelProps, shrink: true }}
                        SelectProps={{
                            displayEmpty: true,
                            /* Without this MUI renders NOTHING for value '' — the field goes
                               blank the moment you choose "no category", which is the same
                               thing it shows when the value is unset or points at a retired
                               category. Three different states, one blank box. Each now says
                               what it is. */
                            renderValue: (value) => {
                                if (value === '' || value === null || value === undefined)
                                    return Localize("VLEARN_NO_CATEGORY", lang)
                                const match = (categories || []).find(c => String(c.id) === String(value))
                                return match ? match.name : Localize("VLEARN_CATEGORY_RETIRED", lang)
                            }
                        }}
                    >
                        {/* An empty value is a legitimate answer: `id_category` is nullable and
                            not every course belongs to a family. */}
                        <MenuItem value=""><Langs str="VLEARN_NO_CATEGORY" /></MenuItem>
                        {(categories || []).map(category => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))}
                        {/* `listCategories` returns only `active = 1` rows, but a course keeps
                            pointing at a category after it is deactivated. Without an option
                            carrying that id, MUI has nothing to match and renders the field
                            EMPTY — indistinguishable from "no category", so the author cannot
                            tell that an association exists, and picking any value silently
                            replaces one they never saw. Disabled, because re-selecting a
                            deactivated category is not something to offer; choosing anything
                            else, or "no category", still works. */}
                        {retiredCategory ? (
                            <MenuItem value={retiredCategory} disabled>
                                <Langs str="VLEARN_CATEGORY_RETIRED" />
                            </MenuItem>
                        ) : null}
                    </TextField>

                    <TextField
                        select size="small" fullWidth
                        label={<Langs str="VLEARN_FIELD_LOCALE" />}
                        value={form.locale}
                        onChange={(event) => edit({ locale: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_LOCALE_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    >
                        {LOCALES.map(item => (
                            <MenuItem key={item.value} value={item.value}>
                                <Langs str={item.key} />
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select size="small" fullWidth
                        label={<Langs str="VLEARN_FIELD_LEVEL" />}
                        value={form.level}
                        onChange={(event) => edit({ level: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_LEVEL_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    >
                        {LEVELS.map(item => (
                            <MenuItem key={item.value} value={item.value}>
                                <Langs str={item.key} />
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        size="small" fullWidth type="number"
                        label={<Langs str="VLEARN_FIELD_ESTIMATED_MINUTES" />}
                        value={form.estimated_minutes}
                        inputProps={{ min: 1, step: 5 }}
                        onChange={(event) => edit({ estimated_minutes: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_ESTIMATED_MINUTES_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                </Box>
            </Box>

            {/* ── Cover ──────────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                {groupTitle('VLEARN_GROUP_COVER', 'VLEARN_GROUP_COVER_HELP')}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Box sx={{
                        width: 220, height: 124, flexShrink: 0,
                        borderRadius: '10px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.08)',
                        backgroundColor: 'rgba(0,0,0,0.03)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        {coverUrl
                            ? <img src={coverUrl} alt={Localize("VLEARN_COVER_ALT", lang)}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <ImageOutlinedIcon sx={{ fontSize: 28, color: 'rgba(0,0,0,0.25)' }} />}
                    </Box>

                    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                        <input
                            ref={fileInput} type="file" accept={COVER_ACCEPT}
                            onChange={onPickCover} style={{ display: 'none' }}
                            aria-hidden="true" tabIndex={-1}
                        />
                        <Button size="small" variant="outlined" disabled={uploading}
                            onClick={() => fileInput.current && fileInput.current.click()}
                            sx={{ textTransform: 'none' }}>
                            <Langs str={coverUrl ? 'VLEARN_COVER_REPLACE' : 'VLEARN_COVER_UPLOAD'} />
                        </Button>
                        <Typography sx={{ ...vlearnType.meta, mt: 1 }}>
                            <Langs str="VLEARN_COVER_FORMATS" />
                        </Typography>

                        {/* A real bar, not a spinner: a cover is small but the same uploader
                            reports on training videos, and the author should learn to trust
                            the number. */}
                        {uploading && uploadProgress !== null ? (
                            <Box sx={{ mt: 1.5 }}>
                                <LinearProgress variant="determinate" value={uploadProgress}
                                    sx={{ height: 6, borderRadius: 3 }} />
                                <Typography sx={{ ...vlearnType.meta, mt: 0.5 }}>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {/* ── Rules ──────────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                {groupTitle('VLEARN_GROUP_RULES', 'VLEARN_GROUP_RULES_HELP')}

                <Box sx={{ ...grid }}>
                    {switchRow('is_mandatory', 'VLEARN_FIELD_MANDATORY', 'VLEARN_FIELD_MANDATORY_HELP')}
                    {switchRow('sequential', 'VLEARN_FIELD_SEQUENTIAL',
                        'VLEARN_FIELD_SEQUENTIAL_HELP_ON', 'VLEARN_FIELD_SEQUENTIAL_HELP_OFF')}
                    {switchRow('certificate_enabled', 'VLEARN_FIELD_CERTIFICATE',
                        'VLEARN_FIELD_CERTIFICATE_HELP')}
                </Box>

                <Box sx={{ mt: 3, maxWidth: 420 }}>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, mb: 0.5 }}>
                        <Langs str="VLEARN_FIELD_PASSING_PERCENT" />
                    </Typography>
                    <Slider
                        value={form.passing_percent}
                        min={1} max={100} step={1}
                        valueLabelDisplay="auto"
                        marks={[{ value: 1, label: '1%' }, { value: 100, label: '100%' }]}
                        // Local state follows the thumb so the label is live, but the write
                        // waits for the drag to end: `onChange` fires per pixel, and one
                        // request per pixel is not a save, it is a flood.
                        onChange={(event, value) => setForm(prev => ({ ...prev, passing_percent: value }))}
                        onChangeCommitted={(event, value) => edit({ passing_percent: value })}
                        aria-label={Localize("VLEARN_FIELD_PASSING_PERCENT", lang)}
                        sx={{ color: 'var(--orange, rgb(255,144,21))' }}
                    />
                    <Typography sx={vlearnType.meta}>
                        <Langs str="VLEARN_FIELD_PASSING_PERCENT_HELP" />
                    </Typography>
                </Box>
            </Box>

            {/* ── Availability ───────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                {groupTitle('VLEARN_GROUP_AVAILABILITY', 'VLEARN_GROUP_AVAILABILITY_HELP')}

                {rangeError ? (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: '0.82rem' }}>
                        <Langs str="VLEARN_RANGE_INVALID" />
                    </Alert>
                ) : null}

                <Box sx={grid}>
                    <TextField
                        size="small" fullWidth type="date"
                        label={<Langs str="VLEARN_FIELD_AVAILABLE_FROM" />}
                        value={form.available_from}
                        onChange={(event) => edit({ available_from: event.target.value })}
                        error={rangeError}
                        helperText={<Langs str="VLEARN_FIELD_AVAILABLE_FROM_HELP" />}
                        sx={inputSx} InputLabelProps={{ ...labelProps, shrink: true }}
                    />
                    <TextField
                        size="small" fullWidth type="date"
                        label={<Langs str="VLEARN_FIELD_AVAILABLE_TO" />}
                        value={form.available_to}
                        onChange={(event) => edit({ available_to: event.target.value })}
                        error={rangeError}
                        helperText={<Langs str="VLEARN_FIELD_AVAILABLE_TO_HELP" />}
                        sx={inputSx} InputLabelProps={{ ...labelProps, shrink: true }}
                    />
                    <TextField
                        size="small" fullWidth type="date"
                        label={<Langs str="VLEARN_FIELD_DUE_DATE" />}
                        value={form.due_date}
                        onChange={(event) => edit({ due_date: event.target.value })}
                        helperText={<Langs str="VLEARN_FIELD_DUE_DATE_HELP" />}
                        sx={inputSx} InputLabelProps={{ ...labelProps, shrink: true }}
                    />
                </Box>

                {/* The audience is a separate tab, and an author setting a due date is
                    thinking about the people who will receive it. A pointer beats a
                    discovery. */}
                <Tooltip title={Localize("VLEARN_DUE_DATE_AUDIENCE_TIP", lang)} arrow>
                    <Chip size="small" sx={{ mt: 2, fontSize: '0.72rem' }}
                        label={<Langs str="VLEARN_DUE_DATE_AUDIENCE_NOTE" />} />
                </Tooltip>
            </Box>
        </Box>
    )
}
